// ─── DrivingEventsView — Alarmes de velocidade (Comportamento) ────────
//
// Montado no menu FROTA (movido de Operações em 27/09) — variante fuel /
// Move Express. O ficheiro mantém-se nesta pasta. O operador GERE os
// alarmes de excesso de velocidade sincronizados do EcooGPS: filtra, marca
// como tratado, anota, e vê no mapa. Fase 1 (MVP).

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../App';
import {
  Box, Typography, Button, IconButton, Chip, Tooltip, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@material-ui/core';
import SyncIcon from '@material-ui/icons/Sync';
import SpeedIcon from '@material-ui/icons/Speed';
import EditIcon from '@material-ui/icons/Edit';
import RoomIcon from '@material-ui/icons/Room';

const STATUS = [
  { key: 'new', pt: 'Novo', en: 'New', color: '#b45309', bg: '#fef3c7' },
  { key: 'reviewed', pt: 'Revisto', en: 'Reviewed', color: '#1d4ed8', bg: '#dbeafe' },
  { key: 'actioned', pt: 'Tratado', en: 'Actioned', color: '#15803d', bg: '#dcfce7' },
  { key: 'dismissed', pt: 'Ignorado', en: 'Dismissed', color: '#64748b', bg: '#f1f5f9' },
];
const statusMeta = (k) => STATUS.find(s => s.key === k) || null;

const toInput = (d) => {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function DrivingEventsView({ lang = 'pt' }) {
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const { token } = useContext(AuthContext);

  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  // Filtros
  const [statusF, setStatusF] = useState('all');
  const [vehicle, setVehicle] = useState('');
  const [minOver, setMinOver] = useState('0');
  const [days, setDays] = useState(7);
  const [noteFor, setNoteFor] = useState(null); // evento a anotar
  const [noteText, setNoteText] = useState('');
  // Vista: eventos (lista) ou comportamento (scorecard).
  const [view, setView] = useState('events');
  const [scoreBy, setScoreBy] = useState('driver');
  const [scoreRows, setScoreRows] = useState([]);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [trendDays, setTrendDays] = useState([]);

  const H = { Authorization: `Bearer ${token}` };
  const periodFrom = useCallback(() => new Date(Date.now() - days * 86400000), [days]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('from', periodFrom().toISOString());
      p.set('limit', '300');
      if (statusF !== 'all') p.set('status', statusF);
      if (vehicle.trim()) p.set('vehicle', vehicle.trim());
      if (Number(minOver) > 0) p.set('minOver', minOver);
      const r = await fetch(`/planner-api/driving-events?${p.toString()}`, { headers: H });
      if (r.ok) { const j = await r.json(); setEvents(j.events || []); setTotal(j.total || 0); setPending(j.pending || 0); }
    } catch (_) { /* silencioso */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusF, vehicle, minOver, days]);

  useEffect(() => { load(); }, [load]);

  // Alerta em tempo real (WS) → refresca a lista automaticamente.
  useEffect(() => {
    const onWS = () => { load(); };
    window.addEventListener('speedAlarmsNeedRefresh', onWS);
    return () => window.removeEventListener('speedAlarmsNeedRefresh', onWS);
  }, [load]);

  const loadScore = useCallback(async () => {
    if (!token) return;
    setScoreLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('from', periodFrom().toISOString());
      p.set('by', scoreBy);
      if (Number(minOver) > 0) p.set('minOver', minOver);
      const r = await fetch(`/planner-api/driving-events/scorecard?${p.toString()}`, { headers: H });
      if (r.ok) { const j = await r.json(); setScoreRows(j.rows || []); }
      // Tendência por dia (mesmo período).
      const tp = new URLSearchParams();
      tp.set('from', periodFrom().toISOString());
      if (vehicle.trim()) tp.set('vehicle', vehicle.trim());
      const tr = await fetch(`/planner-api/driving-events/trend?${tp.toString()}`, { headers: H });
      if (tr.ok) { const tj = await tr.json(); setTrendDays(tj.days || []); }
    } catch (_) { /* silencioso */ } finally { setScoreLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, scoreBy, minOver, days]);

  useEffect(() => { if (view === 'scorecard') loadScore(); }, [view, loadScore]);

  const sync = async () => {
    if (!token) return;
    setSyncing(true); setSyncMsg(null);
    try {
      const r = await fetch(`/planner-api/driving-events/sync?from=${encodeURIComponent(periodFrom().toISOString())}`, { method: 'POST', headers: H });
      const j = await r.json();
      if (r.ok && j.ok) setSyncMsg(T(`${j.synced} novos · ${j.fetched} no EcooGPS`, `${j.synced} new · ${j.fetched} in EcooGPS`));
      else setSyncMsg(j.reason || T('Falha no sync', 'Sync failed'));
      load();
    } catch (_) { setSyncMsg(T('Falha no sync', 'Sync failed')); } finally { setSyncing(false); }
  };

  const patch = async (id, body) => {
    try {
      const r = await fetch(`/planner-api/driving-events/${id}`, { method: 'PATCH', headers: { ...H, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.ok) { const upd = await r.json(); setEvents(prev => prev.map(e => e.id === id ? { ...e, ...upd } : e)); load(); }
    } catch (_) { /* silencioso */ }
  };

  const saveNote = async () => {
    if (!noteFor) return;
    await patch(noteFor.id, { note: noteText });
    setNoteFor(null); setNoteText('');
  };

  const fmtDT = (d) => d ? new Date(d).toLocaleString(lang === 'pt' ? 'pt-PT' : 'en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
  const overColor = (o) => o == null ? '#64748b' : o >= 20 ? '#dc2626' : o >= 10 ? '#d97706' : '#16a34a';

  return (
    <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '14px 18px' }}>
      {/* Cabeçalho */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <SpeedIcon style={{ color: '#d97706' }} />
        <Typography style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
          {T('Alarmes de velocidade', 'Speed alarms')}
        </Typography>
        <Chip size="small" label={`${total}`} style={{ height: 22, fontWeight: 700, background: '#fef3c7', color: '#b45309' }} />
        {pending > 0 && <Chip size="small" label={T(`${pending} por tratar`, `${pending} pending`)} style={{ height: 22, fontWeight: 700, background: '#fee2e2', color: '#b91c1c' }} />}
        {/* Toggle de vista: Eventos | Comportamento */}
        <Box style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
          {[{ id: 'events', label: T('Eventos', 'Events'), icon: '📋' }, { id: 'scorecard', label: T('Comportamento', 'Behavior'), icon: '📊' }].map(v => (
            <Chip key={v.id} label={`${v.icon} ${v.label}`} size="small" onClick={() => setView(v.id)}
              style={{ height: 26, fontSize: '0.72rem', fontWeight: view === v.id ? 700 : 500, cursor: 'pointer',
                background: view === v.id ? '#0f172a' : '#fff', color: view === v.id ? '#fff' : '#475569',
                border: view === v.id ? '1px solid #0f172a' : '1px solid #e2e8f0' }} />
          ))}
        </Box>
        <Box style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {syncMsg && <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>{syncMsg}</Typography>}
          <Button onClick={sync} disabled={syncing} variant="contained" startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon style={{ fontSize: 16 }} />}
            style={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff', borderRadius: 8 }}>
            {syncing ? T('A sincronizar…', 'Syncing…') : T('Sincronizar EcooGPS', 'Sync EcooGPS')}
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      <Box style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" variant="outlined" label={T('Estado', 'Status')} value={statusF} onChange={e => setStatusF(e.target.value)} style={{ minWidth: 130 }}>
          <MenuItem value="all">{T('Todos', 'All')}</MenuItem>
          {STATUS.map(s => <MenuItem key={s.key} value={s.key}>{lang === 'pt' ? s.pt : s.en}</MenuItem>)}
        </TextField>
        <TextField select size="small" variant="outlined" label={T('Gravidade', 'Severity')} value={minOver} onChange={e => setMinOver(e.target.value)} style={{ minWidth: 150 }}>
          <MenuItem value="0">{T('Todos', 'All')}</MenuItem>
          <MenuItem value="10">{T('+10 km/h acima', '+10 km/h over')}</MenuItem>
          <MenuItem value="20">{T('+20 km/h acima', '+20 km/h over')}</MenuItem>
          <MenuItem value="30">{T('+30 km/h acima', '+30 km/h over')}</MenuItem>
        </TextField>
        <TextField select size="small" variant="outlined" label={T('Período', 'Period')} value={days} onChange={e => setDays(Number(e.target.value))} style={{ minWidth: 120 }}>
          <MenuItem value={1}>{T('24 horas', '24 hours')}</MenuItem>
          <MenuItem value={7}>{T('7 dias', '7 days')}</MenuItem>
          <MenuItem value={30}>{T('30 dias', '30 days')}</MenuItem>
        </TextField>
        <TextField size="small" variant="outlined" label={T('Viatura', 'Vehicle')} placeholder={T('matrícula…', 'plate…')} value={vehicle} onChange={e => setVehicle(e.target.value)} style={{ minWidth: 150 }} />
      </Box>

      {/* Tabela (vista Eventos) */}
      {view === 'events' && (
      <Box style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box style={{ padding: 40, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : events.length === 0 ? (
          <Box style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
            {T('Sem alarmes. Clica em "Sincronizar EcooGPS" para trazer os eventos.', 'No alarms. Click "Sync EcooGPS" to fetch events.')}
          </Box>
        ) : (
          <TableContainer component={Paper} style={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[T('Data/hora', 'Date/time'), T('Viatura', 'Vehicle'), T('Velocidade', 'Speed'), T('Excesso', 'Over'), T('Local', 'Location'), T('Estado', 'Status'), ''].map((h, i) => (
                    <TableCell key={i} style={{ fontWeight: 800, fontSize: '0.68rem', color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, background: '#f8fafc' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map(ev => {
                  const sm = statusMeta(ev.status);
                  return (
                    <TableRow key={ev.id} hover>
                      <TableCell style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{fmtDT(ev.occurredAt)}</TableCell>
                      <TableCell style={{ fontSize: '0.78rem', fontWeight: 700 }}>{ev.vehiclePlate || `#${ev.vehicleId || '—'}`}</TableCell>
                      <TableCell style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {ev.speedKmh != null ? `${Math.round(ev.speedKmh)} km/h` : '—'}
                        {ev.speedLimitKmh != null && <span style={{ color: '#94a3b8' }}> / {Math.round(ev.speedLimitKmh)}</span>}
                      </TableCell>
                      <TableCell>
                        {ev.overByKmh != null
                          ? <span style={{ fontWeight: 800, color: overColor(ev.overByKmh) }}>+{Math.round(ev.overByKmh)}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </TableCell>
                      <TableCell style={{ fontSize: '0.74rem', color: '#64748b', maxWidth: 240 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 200, verticalAlign: 'middle' }}>
                          {ev.address || '—'}
                        </span>
                        {ev.lat != null && ev.lng != null && (
                          <Tooltip title={T('Abrir no Google Maps', 'Open in Google Maps')}>
                            <a href={`https://www.google.com/maps?q=${ev.lat},${ev.lng}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 6, color: '#2563eb', verticalAlign: 'middle' }}>
                              <RoomIcon style={{ fontSize: 15 }} />
                            </a>
                          </Tooltip>
                        )}
                        {ev.note && <div style={{ fontSize: '0.66rem', color: '#9a3412', fontStyle: 'italic', marginTop: 1 }}>📝 {ev.note}</div>}
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" variant="outlined" value={ev.status}
                          onChange={e => patch(ev.id, { status: e.target.value })}
                          SelectProps={{ style: { fontSize: '0.72rem', fontWeight: 700, color: sm?.color, background: sm?.bg, borderRadius: 8 } }}
                          style={{ minWidth: 118 }}>
                          {STATUS.map(s => <MenuItem key={s.key} value={s.key} style={{ fontSize: '0.78rem' }}>{lang === 'pt' ? s.pt : s.en}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={T('Nota', 'Note')}>
                          <IconButton size="small" onClick={() => { setNoteFor(ev); setNoteText(ev.note || ''); }}>
                            <EditIcon style={{ fontSize: 16, color: '#2563eb' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      )}

      {/* Scorecard (vista Comportamento) */}
      {view === 'scorecard' && (
        <Box style={{ flex: 1, overflow: 'auto' }}>
          <Box style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>{T('Agrupar por:', 'Group by:')}</Typography>
            {[{ id: 'driver', label: T('Motorista', 'Driver') }, { id: 'vehicle', label: T('Viatura', 'Vehicle') }].map(b => (
              <Chip key={b.id} label={b.label} size="small" onClick={() => setScoreBy(b.id)}
                style={{ height: 24, fontSize: '0.72rem', fontWeight: scoreBy === b.id ? 700 : 500, cursor: 'pointer',
                  background: scoreBy === b.id ? '#f59e0b' : '#fff', color: scoreBy === b.id ? '#fff' : '#475569',
                  border: scoreBy === b.id ? '1px solid #f59e0b' : '1px solid #e2e8f0' }} />
            ))}
            <Typography style={{ fontSize: '0.66rem', color: '#94a3b8', marginLeft: 4 }}>
              {scoreBy === 'driver'
                ? T('motorista identificado pela janela/dia da OT (parcial)', 'driver identified via order window/day (partial)')
                : T('todas as viaturas', 'all vehicles')}
            </Typography>
          </Box>
          {/* Tendência por dia — barras (total; parte grave a vermelho) */}
          {trendDays.length > 0 && (() => {
            const maxC = Math.max(1, ...trendDays.map(d => d.count));
            return (
              <Box style={{ marginBottom: 14, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' }}>
                <Typography style={{ fontSize: '0.68rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                  {T('Tendência de alarmes por dia', 'Daily alarm trend')}
                  <span style={{ fontWeight: 600, color: '#94a3b8', marginLeft: 8 }}>· <span style={{ color: '#f59e0b' }}>■</span> {T('total', 'total')} · <span style={{ color: '#dc2626' }}>■</span> {T('graves', 'severe')}</span>
                </Typography>
                <Box style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 72, overflowX: 'auto', paddingBottom: 2 }}>
                  {trendDays.map(d => {
                    const h = Math.round((d.count / maxC) * 64) + 2;
                    const sh = d.count > 0 ? Math.round((d.severe / d.count) * h) : 0;
                    return (
                      <Box key={d.day} title={`${d.day.slice(8)}/${d.day.slice(5, 7)} · ${d.count} ${T('alarmes', 'alarms')}${d.severe ? ` (${d.severe} ${T('graves', 'severe')})` : ''}`}
                        style={{ flex: '0 0 auto', width: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default' }}>
                        <Box style={{ width: 10, height: h, borderRadius: 3, background: '#fde68a', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', overflow: 'hidden' }}>
                          <Box style={{ height: sh, background: '#dc2626' }} />
                          <Box style={{ height: h - sh, background: '#f59e0b' }} />
                        </Box>
                        <span style={{ fontSize: 7.5, color: '#94a3b8', marginTop: 2 }}>{d.day.slice(8)}</span>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })()}
          {scoreLoading ? (
            <Box style={{ padding: 40, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          ) : scoreRows.length === 0 ? (
            <Box style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>{T('Sem dados no período.', 'No data in period.')}</Box>
          ) : (
            <TableContainer component={Paper} style={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['#', scoreBy === 'driver' ? T('Motorista', 'Driver') : T('Viatura', 'Vehicle'), T('Alarmes', 'Alarms'), T('Excesso médio', 'Avg over'), T('Máximo', 'Max'), T('Graves (≥20)', 'Severe (≥20)'), T('Por tratar', 'Pending')].map((h, i) => (
                      <TableCell key={i} style={{ fontWeight: 800, fontSize: '0.68rem', color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, background: '#f8fafc' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scoreRows.map((r, i) => (
                    <TableRow key={r.key} hover>
                      <TableCell style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 700 }}>{i + 1}</TableCell>
                      <TableCell style={{ fontSize: '0.8rem', fontWeight: 700, color: r.unidentified ? '#94a3b8' : '#0f172a', fontStyle: r.unidentified ? 'italic' : 'normal' }}>{r.key}</TableCell>
                      <TableCell style={{ fontSize: '0.82rem', fontWeight: 800 }}>{r.count}</TableCell>
                      <TableCell style={{ fontSize: '0.78rem' }}>+{r.avgOver} km/h</TableCell>
                      <TableCell><span style={{ fontWeight: 800, color: r.maxOver >= 20 ? '#dc2626' : r.maxOver >= 10 ? '#d97706' : '#16a34a' }}>+{Math.round(r.maxOver)}</span></TableCell>
                      <TableCell>{r.severe > 0 ? <span style={{ fontWeight: 800, color: '#dc2626' }}>{r.severe}</span> : <span style={{ color: '#cbd5e1' }}>0</span>}</TableCell>
                      <TableCell>{r.pending > 0 ? <span style={{ fontWeight: 700, color: '#b45309' }}>{r.pending}</span> : <span style={{ color: '#16a34a' }}>0</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Dialog de nota */}
      <Dialog open={!!noteFor} onClose={() => setNoteFor(null)} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: '0.9rem', fontWeight: 800 }}>{T('Nota do alarme', 'Alarm note')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus multiline minRows={3} fullWidth size="small" variant="outlined"
            placeholder={T('Ex.: falado com o motorista, zona de obras…', 'e.g. spoke with driver, roadworks zone…')}
            value={noteText} onChange={e => setNoteText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteFor(null)} style={{ textTransform: 'none', color: '#64748b' }}>{T('Cancelar', 'Cancel')}</Button>
          <Button onClick={saveNote} variant="contained" style={{ textTransform: 'none', fontWeight: 700, background: '#2563eb', color: '#fff' }}>{T('Guardar', 'Save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
